import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Edit, Save, X, Building2,
  Globe, Phone, MapPin, CheckCircle, XCircle,
  Calendar, Cpu
} from 'lucide-react';
import adminCompanyService from '../services/adminCompanyService';

const SIZES = ['SMALL', 'MEDIUM', 'LARGE', 'ENTERPRISE'];
const SIZE_LABELS = { SMALL: '1–50', MEDIUM: '51–250', LARGE: '251–1000', ENTERPRISE: '1000+' };

const Field = ({ label, value }) => (
  <div>
    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{label}</p>
    <p className="text-gray-900">{value || <span className="text-gray-400 italic">—</span>}</p>
  </div>
);

const InputField = ({ label, name, value, onChange, type = 'text' }) => (
  <div>
    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
      {label}
    </label>
    <input
      type={type}
      name={name}
      value={value || ''}
      onChange={onChange}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
    />
  </div>
);

const CompanyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(searchParams.get('edit') === 'true');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [form, setForm] = useState({});

  useEffect(() => {
    adminCompanyService.getById(id)
      .then(data => {
        setCompany(data);
        setForm({
          name: data.name || '',
          industry: data.industry || '',
          size: data.size || '',
          address: data.address || '',
          city: data.city || '',
          state: data.state || '',
          zipCode: data.zipCode || '',
          country: data.country || '',
          website: data.website || '',
          phone: data.phone || '',
          timezone: data.timezone || '',
          isActive: data.isActive ?? true,
        });
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleCancel = () => {
    setForm({
      name: company.name || '',
      industry: company.industry || '',
      size: company.size || '',
      address: company.address || '',
      city: company.city || '',
      state: company.state || '',
      zipCode: company.zipCode || '',
      country: company.country || '',
      website: company.website || '',
      phone: company.phone || '',
      timezone: company.timezone || '',
      isActive: company.isActive ?? true,
    });
    setSaveError(null);
    setEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await adminCompanyService.update(id, form);
      setCompany(updated);
      setEditing(false);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-600 p-4 rounded-lg">
        Error loading company: {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/companies')}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-r from-primary-600 to-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-lg">
              {company.name?.substring(0, 2).toUpperCase()}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{company.name}</h1>
              <div className="flex items-center gap-2 mt-1">
                {company.isActive ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                    <CheckCircle className="w-3 h-3" /> Active
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                    <XCircle className="w-3 h-3" /> Inactive
                  </span>
                )}
                <span className="text-xs text-gray-400">ID #{company.id}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <button
                onClick={handleCancel}
                disabled={saving}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <X className="w-4 h-4" /> Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </>
          ) : (
            <button
              onClick={() => setEditing(true)}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
            >
              <Edit className="w-4 h-4" /> Edit Company
            </button>
          )}
        </div>
      </div>

      {saveError && (
        <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">{saveError}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Basic Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Building2 className="w-4 h-4" /> Basic Information
            </h2>
            {editing ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField label="Company Name" name="name" value={form.name} onChange={handleChange} />
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Domain</label>
                  <input
                    type="text"
                    value={company.domain || ''}
                    disabled
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-400 cursor-not-allowed"
                  />
                </div>
                <InputField label="Industry" name="industry" value={form.industry} onChange={handleChange} />
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Size</label>
                  <select
                    name="size"
                    value={form.size || ''}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Select size</option>
                    {SIZES.map(s => (
                      <option key={s} value={s}>{SIZE_LABELS[s]} employees</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2 flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="isActive"
                    name="isActive"
                    checked={form.isActive}
                    onChange={handleChange}
                    className="w-4 h-4 text-primary-600 rounded border-gray-300"
                  />
                  <label htmlFor="isActive" className="text-sm text-gray-700">Company is active</label>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Company Name" value={company.name} />
                <Field label="Domain" value={company.domain} />
                <Field label="Industry" value={company.industry} />
                <Field label="Size" value={company.size ? `${SIZE_LABELS[company.size]} employees` : null} />
              </div>
            )}
          </div>

          {/* Contact & Location */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Contact & Location
            </h2>
            {editing ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InputField label="Website" name="website" value={form.website} onChange={handleChange} />
                <InputField label="Phone" name="phone" value={form.phone} onChange={handleChange} />
                <div className="md:col-span-2">
                  <InputField label="Address" name="address" value={form.address} onChange={handleChange} />
                </div>
                <InputField label="City" name="city" value={form.city} onChange={handleChange} />
                <InputField label="State / Province" name="state" value={form.state} onChange={handleChange} />
                <InputField label="ZIP / Postal Code" name="zipCode" value={form.zipCode} onChange={handleChange} />
                <InputField label="Country" name="country" value={form.country} onChange={handleChange} />
                <div className="md:col-span-2">
                  <InputField label="Timezone" name="timezone" value={form.timezone} onChange={handleChange} />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Website" value={company.website} />
                <Field label="Phone" value={company.phone} />
                <div className="md:col-span-2">
                  <Field label="Address" value={company.address} />
                </div>
                <Field label="City" value={company.city} />
                <Field label="State / Province" value={company.state} />
                <Field label="ZIP / Postal Code" value={company.zipCode} />
                <Field label="Country" value={company.country} />
                <div className="md:col-span-2">
                  <Field label="Timezone" value={company.timezone} />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Platform */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Cpu className="w-4 h-4" /> Platform Info
            </h2>
            <div className="space-y-4">
              <Field label="CMMS Tenant ID" value={company.cmmsTenantId} />
              <Field label="Chatbot Tenant ID" value={company.chatbotTenantId} />
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Subscriptions</p>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">CMMS</span>
                    {company.hasCmmsSubscription
                      ? <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">Active</span>
                      : <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">None</span>}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Chatbot</span>
                    {company.hasChatbotSubscription
                      ? <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">Active</span>
                      : <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full">None</span>}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Calendar className="w-4 h-4" /> Dates
            </h2>
            <div className="space-y-4">
              <Field
                label="Created"
                value={company.createdAt ? new Date(company.createdAt).toLocaleString() : null}
              />
              <Field
                label="Last Updated"
                value={company.updatedAt ? new Date(company.updatedAt).toLocaleString() : null}
              />
            </div>
          </div>

          {/* Quick Links */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4 flex items-center gap-2">
              <Globe className="w-4 h-4" /> Quick Links
            </h2>
            <div className="space-y-2">
              {company.website && (
                <a
                  href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-sm text-primary-600 hover:underline truncate"
                >
                  {company.website}
                </a>
              )}
              {company.domain && (
                <p className="text-sm text-gray-600">Domain: <span className="font-medium">{company.domain}</span></p>
              )}
              {!company.website && !company.domain && (
                <p className="text-sm text-gray-400 italic">No links available</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyDetail;
